import React, { useEffect, useRef, useState } from "react";
import { FiCalendar } from "react-icons/fi";
import { AccountModalBody, AccountModalFooter, AccountModalHeader } from "./AccountModalParts";
import { UpdateDob } from "./Account.api";
import { useAuth } from "context/auth-context";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { toastrError, toastrSuccess } from "lib/toastr";
import { useTranslation } from "react-i18next";

const dobRegex = /^\d{4}-(0?[1-9]|1[0-2])-(0?[1-9]|[12][0-9]|3[01])$/;

interface AccountToggleProps {
  dob: string;
  isPopupOpenDob;
}

const DobToggle: React.FC<AccountToggleProps> = ({ isPopupOpenDob }) => {
  const { auth } = useAuth();
  const { t } = useTranslation();
  const modelRef = useRef();
  const [error, setError] = useState("");
  const queryClient = useQueryClient();
  const [day, setDay] = useState("");
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [issubmit, setissubmit] = useState(false);

  const generateOptions = (start, end) => {
    const options = [];
    for (let i = start; i <= end; i++) {
      options.push(
        <option key={i} value={i}>
          {i}
        </option>
      );
    }
    return options;
  };
  const dobFormat = `${year}-${month}-${day}`;
  const mutation = useMutation(() => UpdateDob(dobFormat, auth?.panelistId), {
    onSuccess: (response: any) => {
      if (response.success) {
        toastrSuccess(t("dobTogel.success"));
        queryClient.invalidateQueries(["getAccountDetailsapi"]);
      } else {
        toastrError(t("dobTogel.failed"));
      }
    },
    onError: () => {
      toastrError(t("dobTogel.terminate"));
    },
  });

  const handleSubmit = () => {
    if (!dobRegex.test(dobFormat)) {
      toastrError(t("dobTogel.invalid"));
      return;
    }
    setissubmit(true);
    const dobDate = new Date(dobFormat);
    const now = new Date();
    let age = now.getFullYear() - dobDate.getFullYear();
    const monthDiff = now.getMonth() - dobDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dobDate.getDate())) {
      age--;
    }
    // Age validation
    if (age < 18) {
      setError(t("dobTogel.underage.error"));
      return false;
    } else {
      mutation.mutate();
      isPopupOpenDob();
    }
  };

  useEffect(() => {
    if (issubmit) {
      const dobDate = new Date(dobFormat);
      const now = new Date();
      let age = now.getFullYear() - dobDate.getFullYear();
      const monthDiff = now.getMonth() - dobDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dobDate.getDate())) {
        age--;
      }
      if (age < 18) {
        setError(t("dobTogel.underage.error"));
      }
    }
  }, [t]);

  const CloseModal = (e) => {
    if (modelRef.current === e.target) {
      isPopupOpenDob();
    }
  };
  return (
    <>
      {isPopupOpenDob && (
        <div
          className="zs-backdrop"
          ref={modelRef}
          onClick={CloseModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="account-dob-title"
        >
          <div className="zs-modal max-w-md">
            <AccountModalHeader
              titleId="account-dob-title"
              title={t("dobTogel.head")}
              icon={<FiCalendar />}
              onClose={isPopupOpenDob}
            />

            <AccountModalBody>
              <div>
                <label className="zs-label">{t("dobTogel.lebel.select")}</label>
                <div className="grid grid-cols-3 gap-2">
                  {/* Day Dropdown */}
                  <select value={day} onChange={(e) => setDay(e.target.value)} className="zs-select">
                    <option value="">{t("dobTogel.option.day")}</option>
                    {generateOptions(1, 31)}
                  </select>
                  {/* Month Dropdown */}
                  <select value={month} onChange={(e) => setMonth(e.target.value)} className="zs-select">
                    <option value="">{t("dobTogel.option.month")}</option>
                    {generateOptions(1, 12)}
                  </select>
                  {/* Year Dropdown */}
                  <select value={year} onChange={(e) => setYear(e.target.value)} className="zs-select">
                    <option value="">{t("dobTogel.option.year")}</option>
                    {generateOptions(new Date().getFullYear() - 100, new Date().getFullYear())}
                  </select>
                </div>
                {error ? <p className="zs-error">{error}</p> : <p className="zs-hint">You must be 18 or older.</p>}
              </div>
            </AccountModalBody>
            <AccountModalFooter>
              <button className="zs-btn zs-btn-primary" onClick={handleSubmit} type="button">
                {t("dobTogel.button")}
              </button>
            </AccountModalFooter>
          </div>
        </div>
      )}
    </>
  );
};

export default DobToggle;
